/**
 * ProfileHeader Component
 * Displays user profile information with follow stats and actions
 */
import React from 'react';
import { ProfileAvatar } from '../../shared/components/ProfileAvatar';
import { FollowButton } from '../Social/FollowButton';
import { useFollowStats } from '../../shared/hooks/useFollowStats';
import { IUserStats } from '../../types/profile.types';
import './ProfileHeader.css';

interface ProfileHeaderProps {
  userId: string;
  username: string;
  email?: string;
  bio?: string;
  profileImage?: string | null;
  showFollowButton?: boolean;
  tripStats?: IUserStats | null;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  userId,
  username,
  email,
  bio,
  profileImage,
  showFollowButton = true,
  tripStats
}) => {
  const { stats, loading, incrementFollowers, decrementFollowers } =
    useFollowStats(userId);

  const handleFollowChange = (isFollowing: boolean) => {
    if (isFollowing) {
      incrementFollowers();
    } else {
      decrementFollowers();
    }
  };

  const formatCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <div className='profile-header'>
      <div className='profile-header-content'>
        <div className='profile-header-avatar'>
          <ProfileAvatar
            src={profileImage}
            username={username}
            size='xl'
          />
        </div>

        <div className='profile-header-info'>
          <div className='profile-header-top'>
            <div>
              <h2 className='profile-header-username'>{username}</h2>
              {email && <p className='profile-header-email'>{email}</p>}
            </div>
            {showFollowButton && (
              <FollowButton
                userId={userId}
                initialIsFollowing={stats?.isFollowing}
                onFollowChange={handleFollowChange}
              />
            )}
          </div>

          <div className='profile-header-stats'>
            {loading ? (
              <div
                className='spinner-border spinner-border-sm text-secondary'
                role='status'
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            ) : stats ? (
              <>
                <div className='profile-stat'>
                  <span className='profile-stat-value'>
                    {formatCount(stats.followersCount)}
                  </span>
                  <span className='profile-stat-label'>Followers</span>
                </div>
                <div className='profile-stat-divider' />
                <div className='profile-stat'>
                  <span className='profile-stat-value'>
                    {formatCount(stats.followingCount)}
                  </span>
                  <span className='profile-stat-label'>Following</span>
                </div>
                {tripStats && (
                  <>
                    <div className='profile-stat-divider' />
                    <div className='profile-stat'>
                      <span className='profile-stat-value'>
                        {formatCount(tripStats.totalTrips)}
                      </span>
                      <span className='profile-stat-label'>Trips</span>
                    </div>
                    <div className='profile-stat-divider' />
                    <div className='profile-stat'>
                      <span className='profile-stat-value'>
                        {formatCount(tripStats.publicTrips)}
                      </span>
                      <span className='profile-stat-label'>Public</span>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>

          {bio && <p className='profile-header-bio'>{bio}</p>}
        </div>
      </div>
    </div>
  );
};
